import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./style.scss";

interface IProps {
  anchor: HTMLElement | null,
  content: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  timeout?: number,
}

const Tooltip = ({ anchor, content, timeout, position = 'top' }: IProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculatePosition = useCallback(() => {
    if (!anchor || !tooltipRef.current) return;

    const anchorRect = anchor.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top, left;

    switch (position) {
      case 'top':
        top = anchorRect.top - tooltipRect.height - 8; // 8px for a little gap
        left = anchorRect.right - tooltipRect.width;
        break;
      case 'bottom':
        top = anchorRect.bottom + 8;
        left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'left':
        top = anchorRect.top + (anchorRect.height / 2) - (tooltipRect.height / 2);
        left = anchorRect.left - tooltipRect.width - 8;
        break;
      case 'right':
        top = anchorRect.top + (anchorRect.height / 2) - (tooltipRect.height / 2);
        left = anchorRect.right + 8;
        break;
      default:
        top = anchorRect.top - tooltipRect.height - 8;
        left = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);
    }

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    if (left < 0) left = 0;
    if (top < 0) top = 0;
    if (left + tooltipRect.width > viewportWidth) left = viewportWidth - tooltipRect.width;
    if (top + tooltipRect.height > viewportHeight) top = viewportHeight - tooltipRect.height;


    setTooltipStyle({
      top: `${top + window.scrollY}px`,
      left: `${left + window.scrollX}px`,
      position: 'fixed',
      opacity: 1,
      visibility: 'visible',
    });
  }, [anchor, position]);

  useEffect(() => {
    if (isVisible) {
      calculatePosition();

      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition);

      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition);
      };
    }
  }, [isVisible, calculatePosition]);

  useEffect(() => {
    if (anchor) {
      const showTooltip = () => {
        setIsVisible(true);
        if (!timeoutRef.current && timeout) {
          timeoutRef.current = setTimeout(() => setIsVisible(false), timeout);
        }
      }
      const hideTooltip = () => {
        setIsVisible(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }

      anchor.addEventListener('mouseenter', showTooltip);
      anchor.addEventListener('mouseleave', hideTooltip);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        anchor.removeEventListener('mouseenter', showTooltip);
        anchor.removeEventListener('mouseleave', hideTooltip);
      };
    }
  }, [anchor, timeout]);

  if (!isVisible || !anchor) {
    return null;
  }

  return (
    <>
      {createPortal(
        <div
          className={`B-tooltip B-tooltip-${position}`}
          ref={tooltipRef}
          style={tooltipStyle}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip;